import { CreateLabRequest, LabDTO, UpdateLabRequest } from '@/models';
import { useGroupStore, useInstitutionStore, useLabStore, useUIStore } from '@/store';
import React, { useCallback, useEffect, useState } from 'react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Manages lab CRUD operations, pagination, and modal state
 * for the Lab Management page
 */
export const useLabManagement = () => {
  const {
    labs,
    pagination,
    isLoading,
    error,
    fetchLabs,
    createLab,
    updateLab,
    deleteLab,
    clearError,
  } = useLabStore();

  const { institutions, fetchInstitutions } = useInstitutionStore();
  const { groups, fetchGroups } = useGroupStore();
  const { addNotification } = useUIStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedLab, setSelectedLab] = useState<LabDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [labToDelete, setLabToDelete] = useState<LabDTO | null>(null);
  const [formData, setFormData] = useState<CreateLabRequest>({
    institutionId: '',
    groupId: '',
    name: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchLabs(currentPage, pageSize);
    fetchInstitutions(1, 100);
    fetchGroups(1, 100);
  }, [currentPage, pageSize, fetchLabs, fetchInstitutions, fetchGroups]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const handleCreateClick = useCallback(() => {
    setModalMode('create');
    setSelectedLab(null);
    setFormData({
      institutionId: '',
      groupId: '',
      name: '',
    });
    setShowModal(true);
    clearError();
  }, [clearError]);

  const handleEditClick = useCallback(
    (lab: LabDTO) => {
      setModalMode('edit');
      setSelectedLab(lab);
      setFormData({
        institutionId: lab.institutionId,
        groupId: lab.groupId || '',
        name: lab.name || '',
      });
      setShowModal(true);
      clearError();
    },
    [clearError]
  );

  const handleDeleteClick = useCallback(
    (lab: LabDTO) => {
      setLabToDelete(lab);
      setShowDeleteModal(true);
      clearError();
    },
    [clearError]
  );

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    try {
      if (modalMode === 'create') {
        await createLab(formData);
        addNotification({ type: 'success', message: 'Lab created successfully', duration: 2000 });
      } else if (selectedLab) {
        const updateData: UpdateLabRequest = {
          institutionId: selectedLab.institutionId,
          name: formData.name,
          groupId: formData.groupId,
        };
        await updateLab(selectedLab.institutionId, selectedLab.id, updateData);
        addNotification({ type: 'success', message: 'Lab updated successfully', duration: 2000 });
      }
      setShowModal(false);
      await fetchLabs(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    modalMode,
    createLab,
    formData,
    selectedLab,
    updateLab,
    fetchLabs,
    currentPage,
    pageSize,
    addNotification,
  ]);

  const handleDelete = useCallback(async () => {
    if (!labToDelete) return;
    setIsSaving(true);
    try {
      await deleteLab(labToDelete.institutionId, labToDelete.id);
      addNotification({ type: 'success', message: 'Lab deleted successfully', duration: 2000 });
      setShowDeleteModal(false);
      await fetchLabs(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [labToDelete, deleteLab, fetchLabs, currentPage, pageSize, addNotification]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleInstitutionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, institutionId: e.target.value, groupId: '' }));
  }, []);

  const closeModal = useCallback(() => setShowModal(false), []);
  const closeDeleteModal = useCallback(() => setShowDeleteModal(false), []);

  return {
    labs,
    institutions,
    groups,
    pagination,
    isLoading,
    error,
    currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showModal,
    modalMode,
    showDeleteModal,
    labToDelete,
    formData,
    isSaving,
    setCurrentPage,
    handlePageSizeChange,
    handleCreateClick,
    handleEditClick,
    handleDeleteClick,
    handleSubmit,
    handleDelete,
    handleInputChange,
    handleInstitutionChange,
    closeModal,
    closeDeleteModal,
    clearError,
  };
};
